import { db, auth } from '../firebaseConfig.js';
import {
  collection,
  query,
  where,
  doc,
  serverTimestamp,
  getCountFromServer,
  getDocsFromCache,
  runTransaction,
  increment,
} from 'firebase/firestore';

// Get likes for a comment and whether current user liked
export async function getCommentLikes(commentId, userId) {
  const lowQuota = process.env.REACT_APP_LOW_QUOTA_MODE === 'true';
  const likesRef = collection(db, 'solution_reason_comment_likes');
  let likes = 0;
  const countQuery = query(likesRef, where('commentId', '==', commentId));

  // Try cache-first count to avoid server reads
  try {
    const cached = await getDocsFromCache(countQuery);
    if (cached && !cached.empty) {
      likes = cached.size;
    }
  } catch (_) { /* ignore cache miss */ }

  // If not low quota and likes still 0, try server aggregation
  if (!lowQuota && likes === 0) {
    try {
      const totalCountSnap = await getCountFromServer(countQuery);
      likes = totalCountSnap.data().count || 0;
    } catch (e) {
      console.warn('[getCommentLikes] getCountFromServer failed:', e?.code || e?.message || e);
    }
  }

  // Check if the current user liked (via transaction result or separate read)
  let userLiked = false;
  if (userId) {
    const likeId = `${commentId}_${userId}`;
    const likeDocRef = doc(db, 'solution_reason_comment_likes', likeId);
    try {
      const snap = await runTransaction(db, async (tx) => tx.get(likeDocRef));
      userLiked = snap.exists();
    } catch (_) {
      // fallback: treat as not liked
    }
  }
  return { commentId, likes, userLiked };
}

/**
 * BUG 6 fix: Toggle like using a Firestore transaction to eliminate race condition.
 * Previously used getDoc + setDoc/deleteDoc which allowed two concurrent calls
 * to both read !existing.exists() and both create duplicate like documents.
 *
 * @param {string} commentId
 * @param {string} userId  (ignored — overridden by auth.currentUser.uid for security)
 * @returns {Promise<{ commentId: string, likes: number, userLiked: boolean }>}
 */
export async function likeComment(commentId, userId) {
  const authUid = auth?.currentUser?.uid;
  if (!authUid) {
    const err = new Error('Not authenticated');
    console.error('[likeComment] No authenticated Firebase user present');
    throw err;
  }
  const uid = authUid;
  const likeId = `${commentId}_${uid}`;
  const likeDocRef = doc(db, 'solution_reason_comment_likes', likeId);
  const commentRef = doc(db, 'solution_reason_comments', commentId);

  try { await auth.currentUser.getIdToken(); } catch (_) {}

  await runTransaction(db, async (tx) => {
    const likeSnap = await tx.get(likeDocRef);
    if (!likeSnap.exists()) {
      console.debug('[likeComment] Creating like', { commentId, uid, likeId });
      tx.set(likeDocRef, { commentId, userId: uid, timestamp: serverTimestamp() });
      // Increment denormalized counter if comment doc exists
      const commentSnap = await tx.get(commentRef);
      if (commentSnap.exists()) {
        tx.update(commentRef, { likes: increment(1) });
      }
    }
    // If already liked, no-op (idempotent)
  });

  return getCommentLikes(commentId, uid);
}

/**
 * BUG 6 fix: Unlike using a Firestore transaction.
 *
 * @param {string} commentId
 * @param {string} userId  (ignored — overridden by auth.currentUser.uid for security)
 * @returns {Promise<{ commentId: string, likes: number, userLiked: boolean }>}
 */
export async function unlikeComment(commentId, userId) {
  const authUid = auth?.currentUser?.uid;
  if (!authUid) {
    const err = new Error('Not authenticated');
    console.error('[unlikeComment] No authenticated Firebase user present');
    throw err;
  }
  const uid = authUid;
  const likeId = `${commentId}_${uid}`;
  const likeDocRef = doc(db, 'solution_reason_comment_likes', likeId);
  const commentRef = doc(db, 'solution_reason_comments', commentId);

  try { await auth.currentUser.getIdToken(); } catch (_) {}

  await runTransaction(db, async (tx) => {
    const likeSnap = await tx.get(likeDocRef);
    if (likeSnap.exists()) {
      console.debug('[unlikeComment] Deleting like', { commentId, uid, likeId });
      tx.delete(likeDocRef);
      // Decrement denormalized counter if comment doc exists
      const commentSnap = await tx.get(commentRef);
      if (commentSnap.exists()) {
        tx.update(commentRef, { likes: increment(-1) });
      }
    }
    // If already unliked, no-op (idempotent)
  });

  return getCommentLikes(commentId, uid);
}
